import type { ClientState, MessengerClient, Platform, PlatformAppInfo, ServerEntry } from '@sd/client-core';
import type { FileInfo } from '@sd/contracts';
import type { UpdateInfo } from '@sd/contracts/ipc';
import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import type { ThemePreference } from '../ui/theme';

/** The active server session. */
export const ClientContext = createContext<MessengerClient | null>(null);

export function useClient(): MessengerClient {
  const client = useContext(ClientContext);
  if (!client) throw new Error('useClient outside ClientContext');
  return client;
}

/** Subscribes to a slice of the client store. Selectors must return stable references. */
export function useClientState<T>(selector: (state: ClientState) => T): T {
  const client = useClient();
  return useSyncExternalStore(client.store.subscribe, () => selector(client.store.getState()), () => selector(client.store.getState()));
}

export type ModalState =
  | { kind: 'people'; mode: 'new' | 'add-member' | 'add-peer'; preselect?: string[] }
  | { kind: 'invite' }
  | { kind: 'search'; scope?: string | null; tab?: 'saved' }
  | { kind: 'settings' }
  | { kind: 'servers' }
  /** `gallery`: the pictures to swipe through (else the ones of the file's conversation loaded here). */
  | { kind: 'preview'; file: FileInfo; gallery?: FileInfo[] }
  | { kind: 'forward'; conversationId: string; messageId: string }
  | null;

/** Host-level facts and navigation shared by every messenger component. */
export interface AppEnv {
  platform: Platform;
  appInfo: PlatformAppInfo;
  servers: ServerEntry[];
  activeServerId: string | null;
  /** Unread totals of the other signed-in servers (rail badges). */
  serverUnread: Record<string, number>;
  switchServer(serverId: string): void;
  /** Opens the connect flow to add another company server (desktop). */
  addServer(): void;
  navigate(screen: 'admin' | 'download'): void;
  now(): Date;
  theme: { preference: ThemePreference; setPreference(value: ThemePreference): void };
  modal: ModalState;
  openModal(modal: Exclude<ModalState, null>): void;
  closeModal(): void;
  /** A newer app version the desktop host verified (R39), whichever server is shown. */
  update: UpdateInfo | null;
  /**
   * After saving every server's outbox, restarts into it or hands the checked installer to the
   * system installer (which quits the app); otherwise opens the download page.
   */
  installUpdate(): Promise<void>;
  /**
   * "완전히 종료" (desktop): after saving every server's outbox, asks (naming what would be lost where
   * no key store keeps it) and quits.
   */
  quitApp(): Promise<void>;
}

export const AppEnvContext = createContext<AppEnv | null>(null);

/**
 * What message rows and their attachments ask of the host: the clock and opening a dialog. One object for the
 * app's life: AppEnv changes whenever a dialog opens or closes, and every row reading it was drawn again then
 * (500 rows when the photo viewer opened, as it began to animate).
 */
export interface AppActions {
  now(): Date;
  openModal(modal: Exclude<ModalState, null>): void;
}

export const AppActionsContext = createContext<AppActions | null>(null);

export function useAppActions(): AppActions {
  const actions = useContext(AppActionsContext);
  if (!actions) throw new Error('useAppActions outside AppActionsContext');
  return actions;
}

export function useAppEnv(): AppEnv {
  const env = useContext(AppEnvContext);
  if (!env) throw new Error('useAppEnv outside AppEnvContext');
  return env;
}

/** Re-renders every `ms` so relative time labels stay fresh. */
export function useTick(ms = 60_000): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return tick;
}
